import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import DatePicker from '@cloudscape-design/components/date-picker';
import Header from '@cloudscape-design/components/header';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type {
  ComputeFinanceBalanceResult,
  FinanceAccount,
  FinanceAccountBalance,
} from '@etiquetachef/types';
import { subscribeFinanceAccounts } from '@/lib/firestoreFinance';
import { computeFinanceBalanceCallable } from '@/lib/callables';
import { formatCurrencyCents } from '@/features/dashboard-finance/formatters';
import { SignedCents, TagBreakdownTable } from '@/features/finance-analysis/financeAnalysisShared';
import { dayNoonUtcMs, msToDateInput } from '@/features/finance-analysis/financeAnalysisFormat';
import { serverNow } from '@etiquetachef/ui-shared/online';

const CURRENCIES = ['BRL', 'EUR', 'USD'] as const;

/**
 * Tela `/finance/balance`: posição de saldo do razão interno numa data, por
 * moeda, por conta e por tag.
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro — telas Visão geral,
 * Caixa e Balanço).
 *
 * Contrato: callable `computeFinanceBalance` (leitura, shim
 * `requireSuperAdmin` — qualquer papel admin, `docs/permissions.md`) com
 * `asOf` (meio-dia UTC do dia escolhido), `accountId` e `currency`
 * opcionais; recalcula a cada filtro, descartando resposta atrasada
 * (`alive`). Falha → `Alert` (só `console.error`). Nomes das contas vêm de
 * `subscribeFinanceAccounts`. Sem escrita.
 *
 * Usado por: `routes/router.tsx` (rota `finance/balance`, dentro do
 * `RequireSuperAdmin`), menu Financeiro → "Balanço" (`AdminLayout`). Só
 * `apps/admin` (web). Spec: `docs/screens/finance-balance-admin.md`.
 *
 * Arquitetura: o cálculo é do servidor (Admin SDK sobre `finance_entries` e
 * `finance_accounts`); a tela não lê lançamentos. O corte do dia é no fuso
 * de cada conta (#105) — por isso o `asOf` vai como meio-dia UTC.
 *
 * Dados: `ComputeFinanceBalanceResult` (`packages/types/src/finance.ts`) —
 * `byCurrency[]`/`byAccount[]` com `openingBalanceCents`, `inCents`,
 * `outCents`, `balanceCents` (= abertura + entradas − saídas) em CENTAVOS,
 * `byTag[]` sem saldo de abertura, `capped` quando o servidor bateu o teto.
 *
 * Falhas conhecidas:
 *  - 2026-07-02 (#105): fim-de-dia local incluía lançamentos do dia
 *    seguinte no fuso da conta; corrigido com meio-dia UTC + corte no
 *    servidor.
 *
 * Issues: #76 (origem), #105 (corte por fuso).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function FinanceBalancePage(): React.JSX.Element {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [asOf, setAsOf] = useState(msToDateInput(serverNow()));
  const [accountId, setAccountId] = useState('');
  const [currency, setCurrency] = useState('');
  const [result, setResult] = useState<ComputeFinanceBalanceResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const unsub = subscribeFinanceAccounts((r) => {
      if (r.kind === 'READY') setAccounts(r.accounts);
    });
    return (): void => {
      unsub();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    computeFinanceBalanceCallable({
      // Meio-dia UTC do dia escolhido: o corte do servidor é por dia no fuso
      // da conta (#105); fim-de-dia local incluiria lançamentos de amanhã.
      ...(dayNoonUtcMs(asOf) !== undefined ? { asOf: dayNoonUtcMs(asOf) } : {}),
      ...(accountId !== '' ? { accountId } : {}),
      ...(currency !== '' ? { currency: currency as 'BRL' | 'EUR' | 'USD' } : {}),
    })
      .then((r) => {
        if (alive) setResult(r);
      })
      .catch((err: unknown) => {
        console.error('computeFinanceBalance failed', err);
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return (): void => {
      alive = false;
    };
  }, [asOf, accountId, currency]);

  const accountNameById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a.name])),
    [accounts],
  );

  const accountOptions: SelectProps.Option[] = [
    { value: '', label: t('admin.finance.analysis.filters.allAccounts') },
    ...accounts.map((a) => ({ value: a.id, label: a.name })),
  ];
  const currencyOptions: SelectProps.Option[] = [
    { value: '', label: t('admin.finance.analysis.filters.allCurrencies') },
    ...CURRENCIES.map((c) => ({ value: c, label: c })),
  ];

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.finance.balance.subtitle')}>
          {t('admin.finance.balance.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container>
          <SpaceBetween size="xs" direction="horizontal">
            <DatePicker
              value={asOf}
              onChange={({ detail }): void => {
                setAsOf(detail.value);
              }}
              placeholder={t('admin.finance.balance.asOf')}
            />
            <Select
              selectedOption={accountOptions.find((o) => o.value === accountId) ?? null}
              options={accountOptions}
              onChange={({ detail }): void => {
                setAccountId(detail.selectedOption.value ?? '');
              }}
            />
            <Select
              selectedOption={currencyOptions.find((o) => o.value === currency) ?? null}
              options={currencyOptions}
              onChange={({ detail }): void => {
                setCurrency(detail.selectedOption.value ?? '');
              }}
            />
          </SpaceBetween>
        </Container>

        {error && <Alert type="error">{t('admin.finance.analysis.errorLoad')}</Alert>}
        {result?.capped === true && (
          <Alert type="warning">{t('admin.finance.analysis.capped')}</Alert>
        )}

        {(result?.byCurrency ?? []).map((c) => (
          <Container key={c.currency} header={<Header variant="h3">{c.currency}</Header>}>
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">{t('admin.finance.balance.kpi.opening')}</Box>
                <Box variant="awsui-value-large">
                  {formatCurrencyCents(c.openingBalanceCents, c.currency)}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{t('admin.finance.analysis.columns.in')}</Box>
                <Box variant="awsui-value-large" color="text-status-success">
                  {formatCurrencyCents(c.inCents, c.currency)}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{t('admin.finance.analysis.columns.out')}</Box>
                <Box variant="awsui-value-large" color="text-status-error">
                  {formatCurrencyCents(c.outCents, c.currency)}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{t('admin.finance.balance.kpi.balance')}</Box>
                <Box
                  variant="awsui-value-large"
                  color={c.balanceCents < 0 ? 'text-status-error' : 'text-status-success'}
                >
                  {formatCurrencyCents(c.balanceCents, c.currency)}
                </Box>
              </div>
            </ColumnLayout>
          </Container>
        ))}

        <Table<FinanceAccountBalance>
          items={result?.byAccount ?? []}
          loading={loading}
          trackBy="accountId"
          variant="container"
          header={<Header variant="h3">{t('admin.finance.balance.byAccount')}</Header>}
          empty={
            <Box textAlign="center" color="text-status-inactive" padding="m">
              {t('admin.finance.analysis.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'account',
              header: t('admin.finance.analysis.columns.account'),
              cell: (a): React.ReactNode => accountNameById.get(a.accountId) ?? a.accountId,
            },
            {
              id: 'currency',
              header: t('admin.finance.analysis.columns.currency'),
              cell: (a): React.ReactNode => a.currency,
            },
            {
              id: 'opening',
              header: t('admin.finance.balance.kpi.opening'),
              cell: (a): React.ReactNode => (
                <Box textAlign="right">
                  {formatCurrencyCents(a.openingBalanceCents, a.currency)}
                </Box>
              ),
            },
            {
              id: 'in',
              header: t('admin.finance.analysis.columns.in'),
              cell: (a): React.ReactNode => (
                <Box textAlign="right">{formatCurrencyCents(a.inCents, a.currency)}</Box>
              ),
            },
            {
              id: 'out',
              header: t('admin.finance.analysis.columns.out'),
              cell: (a): React.ReactNode => (
                <Box textAlign="right">{formatCurrencyCents(a.outCents, a.currency)}</Box>
              ),
            },
            {
              id: 'balance',
              header: t('admin.finance.balance.kpi.balance'),
              cell: (a): React.ReactNode => (
                <SignedCents cents={a.balanceCents} currency={a.currency} />
              ),
            },
          ]}
        />

        <TagBreakdownTable
          items={result?.byTag ?? []}
          title={t('admin.finance.balance.byTag')}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}
